/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { connect, ConnectedProps } from 'react-redux';

import BlockIcon from '@mui/icons-material/Block';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { Alert, CircularProgress } from '@mui/material';

import { AlignCenter } from '../../components/AlignCenter/AlignCenter';
import {
  HUMaterialTable,
  HUMaterialTableProps,
  iconCellStyles,
} from '../../components/HUMaterialTable/HUMaterialTable';
import { HUSpin } from '../../components/HUSpin/HUSpin';
import { SimpleConfirmModal } from '../../components/SimpleConfirmModal/SimpleConfirmModal';
import { SimpleErrorModal } from '../../components/SimpleErrorModal/SimpleErrorModal';
import { SimpleNotifyModal } from '../../components/SimpleNotifyModal/SimpleNotifyModal';
import { Namespaces } from '../../i18n';
import { AuthenticationApiService } from '../../services/AuthenticationApiService';
import { SuperuserUserApiService } from '../../services/SuperuserUserApiService';
import { RootState } from '../../store';
import { ApiClientError } from '../../types/api/ApiClientError';
import { SUUser } from '../../types/superuser/user/SUUser';
import { adminFetchUsers } from '../../utils/queryFns';
import { reactToUpdateUserStatusFromAdminPanel } from '../../utils/reactions';
import { searchSubstring } from '../../utils/searchSubstring';
import {
  adminSuperuserStatusDataInitialValues,
  adminUserStatusDataInitialValues,
  forgotPasswdDataInitialValues,
} from './AdminUsersPage.consts';
import { useAdminUsersPageStyles } from './AdminUsersPage.styles';
import { AdminSuperuserStatusDataType, AdminUserStatusDataType, ForgotPasswdDataType } from './AdminUsersPage.types';
import { UsersDetailPanel } from './components/UsersDetailPanel/UsersDetailPanel';

const mapState = (state: RootState) => ({
  auth: state.auth,
});

const connector = connect(mapState);

type PropsFromRedux = ConnectedProps<typeof connector>;

const AdminUsersPageComponent = memo<PropsFromRedux>(props => {
  const { auth } = props;
  const classes = useAdminUsersPageStyles();
  const { t } = useTranslation(Namespaces.Admin);

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const [userStatusData, setUserStatusData] = useState<AdminUserStatusDataType>(adminUserStatusDataInitialValues);

  const [superuserStatusData, setSuperuserStatusData] = useState<AdminSuperuserStatusDataType>(
    adminSuperuserStatusDataInitialValues,
  );

  const [forgotPasswdData, setForgotPasswdData] = useState<ForgotPasswdDataType>(forgotPasswdDataInitialValues);

  const handleUserStatusData = (event: any, rowData: SUUser | SUUser[]) => {
    const e = Array.isArray(rowData) ? rowData[0] : rowData;
    setUserStatusData({
      user: e,
      name: `${e.firstName} ${e.lastName}`,
      isModalOpened: true,
      isErrorModalOpened: false,
      errorMessage: '',
    });
  };

  const handleSuperuserStatusData = (event: any, rowData: SUUser | SUUser[]) => {
    const e = Array.isArray(rowData) ? rowData[0] : rowData;
    setSuperuserStatusData({
      user: e,
      name: `${e.firstName} ${e.lastName}`,
      isModalOpened: true,
      isErrorModalOpened: false,
      errorMessage: '',
    });
  };

  const handleForgotPasswdData = (event: any, rowData: SUUser | SUUser[]) => {
    const e = Array.isArray(rowData) ? rowData[0] : rowData;
    setForgotPasswdData({
      email: e.email,
      name: `${e.firstName} ${e.lastName}`,
      isModalOpened: true,
      isErrorModalOpened: false,
      isSuccess: false,
    });
  };

  const usersQuery = useQuery(adminFetchUsers.key, adminFetchUsers.fn, {
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  const users = useMemo(() => usersQuery.data?.payload ?? [], [usersQuery]);

  const isUsersLoading = useMemo(() => usersQuery.status === 'loading', [usersQuery.status]);

  const isError = useMemo(
    () => usersQuery.error && usersQuery.status === 'error',
    [usersQuery.error, usersQuery.status],
  );

  const getStatusIcon = useCallback(
    (status: boolean | undefined) =>
      status ? (
        <CheckCircleIcon data-cy="status-icon-success" className={classes.success} />
      ) : (
        <CancelIcon data-cy="status-icon-failure" className={classes.failure} />
      ),
    [classes.failure, classes.success],
  );

  const onChangeUserStatus = async () => {
    setIsUpdating(true);
    setUserStatusData({
      ...userStatusData,
      isModalOpened: false,
    });
    try {
      if (userStatusData.user) {
        await SuperuserUserApiService.updateUserStatus({
          userId: userStatusData.user.id,
          isDisabled: !userStatusData.user.isDisabled,
        });
        setUserStatusData(adminUserStatusDataInitialValues);
        await reactToUpdateUserStatusFromAdminPanel();
        setIsUpdating(false);
      }
    } catch (e) {
      const error = e as ApiClientError;
      const errorMessage = error.response?.data.message;
      setUserStatusData({
        ...adminUserStatusDataInitialValues,
        isModalOpened: false,
        isErrorModalOpened: true,
        errorMessage: errorMessage ?? '',
      });
      setIsUpdating(false);
    }
  };

  const onChangeSuperuserStatus = async () => {
    setIsUpdating(true);
    setSuperuserStatusData({
      ...superuserStatusData,
      isModalOpened: false,
    });
    try {
      if (superuserStatusData.user) {
        await SuperuserUserApiService.updateSuperuserStatus({
          userId: superuserStatusData.user.id,
          isSuperuser: !superuserStatusData.user.isSuperuser,
        });
        setSuperuserStatusData(adminSuperuserStatusDataInitialValues);
        await reactToUpdateUserStatusFromAdminPanel();
        setIsUpdating(false);
      }
    } catch (e) {
      const error = e as ApiClientError;
      const errorMessage = error.response?.data.message;

      setSuperuserStatusData({
        ...adminSuperuserStatusDataInitialValues,
        isModalOpened: false,
        isErrorModalOpened: true,
        errorMessage: errorMessage ?? '',
      });
      setIsUpdating(false);
    }
  };

  const onForgotPasswd = async () => {
    setIsUpdating(true);
    setForgotPasswdData({
      ...forgotPasswdData,
      isModalOpened: false,
    });
    try {
      if (forgotPasswdData.email !== null) {
        await AuthenticationApiService.forgot(forgotPasswdData.email);
        setForgotPasswdData({
          ...forgotPasswdData,
          isModalOpened: false,
          isSuccess: true,
        });
        setIsUpdating(false);
      }
    } catch (e) {
      const error = e as ApiClientError;
      console.error(e);
      setForgotPasswdData({
        ...forgotPasswdData,
        isErrorModalOpened: true,
      });
      if (error.response?.data.message === 'unprocessable_entity') {
        setErrorMessage(error.response.data.message);
      }
      setIsUpdating(false);
    }
  };

  const tableConfig = useMemo<HUMaterialTableProps<SUUser>>(
    () => ({
      columns: [
        {
          title: t('users.table.labels.name'),
          render: rowData => (
            <>
              {`${rowData.firstName} ${rowData.lastName}`}
              &nbsp;
              {rowData.id === auth.user?.id && <span className={classes.youHint}>({t('users.table.hints.you')})</span>}
            </>
          ),
          cellStyle: {
            color: '#1F3F6D',
            fontWeight: 500,
          },
          customFilterAndSearch: (term, rowData) => searchSubstring(term, `${rowData.firstName} ${rowData.lastName}`),
        },
        {
          title: t('users.table.labels.email'),
          field: 'email',
        },
        {
          title: t('users.table.labels.superuser'),
          render: rowData => getStatusIcon(rowData.isSuperuser),
          cellStyle: iconCellStyles,
          headerStyle: iconCellStyles,
        },
        {
          title: t('users.table.labels.activated'),
          render: rowData => getStatusIcon(!rowData.isDisabled),
          cellStyle: iconCellStyles,
          headerStyle: iconCellStyles,
        },
        {
          title: t('users.table.labels.verified'),
          render: rowData => getStatusIcon(rowData.verified),
          cellStyle: iconCellStyles,
          headerStyle: iconCellStyles,
        },
      ],
      data: users,
      title: t('users.title'),
      actions: [
        rowData => {
          if (rowData.isSuperuser) {
            return {
              icon: () => <StarIcon data-cy="user-actions-remove-superuser-rights" />,
              tooltip: t('users.table.actions.remove_superuser_rights'),
              onClick: handleSuperuserStatusData,
            };
          }
          return {
            icon: () => <StarBorderIcon data-cy="user-actions-add-superuser-rights" />,
            tooltip: t('users.table.actions.add_superuser_rights'),
            onClick: handleSuperuserStatusData,
          };
        },
        rowData => {
          if (rowData.isDisabled) {
            return {
              icon: () => <CheckCircleOutlineIcon data-cy="user-actions-reactivate-account" />,
              tooltip: t('users.table.actions.reactivate'),
              onClick: handleUserStatusData,
            };
          }
          return {
            icon: () => <BlockIcon data-cy="user-actions-deactivate-account" />,
            tooltip: t('users.table.actions.deactivate'),
            onClick: handleUserStatusData,
          };
        },
        {
          icon: () => <VpnKeyIcon />,
          tooltip: t('users.table.actions.forgot_passwd'),
          onClick: handleForgotPasswdData,
        },
      ],
      options: {
        actionsColumnIndex: -1,
        draggable: false,
        sorting: false,
        search: true,
      },
      detailPanel: [
        {
          tooltip: t('users.table.labels.show_more'),
          render: ({ rowData }) => <UsersDetailPanel rowData={rowData} />,
        },
      ],
    }),
    [t, users, auth.user, classes.youHint, getStatusIcon],
  );

  return (
    <>
      {isUsersLoading ? (
        <AlignCenter className={classes.loaderContainer}>
          <CircularProgress color="primary" size={40} />
        </AlignCenter>
      ) : isError ? (
        <Alert severity="error">{t(`${Namespaces.Common}:errors.unexpected_error`)}</Alert>
      ) : (
        <HUSpin spinning={isUpdating} delay={50}>
          <div className={classes.root}>
            <HUMaterialTable {...tableConfig} />
          </div>
        </HUSpin>
      )}
      <SimpleConfirmModal
        open={userStatusData.isModalOpened}
        message={
          !userStatusData.user?.isDisabled
            ? t('users.confirmations.deactivate', {
                user: userStatusData.name,
              })
            : t('users.confirmations.reactivate', {
                user: userStatusData.name,
              })
        }
        onCancel={() => setUserStatusData(adminUserStatusDataInitialValues)}
        onConfirm={onChangeUserStatus}
      />
      <SimpleConfirmModal
        open={superuserStatusData.isModalOpened}
        message={
          superuserStatusData.user?.isSuperuser
            ? t('users.confirmations.deactivate_superuser', {
                user: superuserStatusData.name,
              })
            : t('users.confirmations.reactivate_superuser', {
                user: superuserStatusData.name,
              })
        }
        onCancel={() => setSuperuserStatusData(adminSuperuserStatusDataInitialValues)}
        onConfirm={onChangeSuperuserStatus}
      />
      <SimpleConfirmModal
        open={forgotPasswdData.isModalOpened}
        message={t('users.confirmations.forgot_passwd', {
          email: forgotPasswdData.email,
          userName: forgotPasswdData.name,
        })}
        onCancel={() => setForgotPasswdData(forgotPasswdDataInitialValues)}
        onConfirm={onForgotPasswd}
      />
      <SimpleErrorModal
        open={userStatusData.isErrorModalOpened}
        message={`${Namespaces.Admin}:users.errors.user.${userStatusData.errorMessage}`}
        onOk={() => setUserStatusData(adminUserStatusDataInitialValues)}
      />
      <SimpleErrorModal
        open={superuserStatusData.isErrorModalOpened}
        message={`${Namespaces.Admin}:users.errors.superuser_rights.${superuserStatusData.errorMessage}`}
        onOk={() => setSuperuserStatusData(adminSuperuserStatusDataInitialValues)}
      />
      <SimpleNotifyModal
        open={forgotPasswdData.isErrorModalOpened}
        message={t([
          `${Namespaces.Admin}:users.errors.forgot_passwd.${errorMessage}`,
          `${Namespaces.Common}:errors.unexpected_error`,
        ])}
        onOk={() => setForgotPasswdData(forgotPasswdDataInitialValues)}
      />
      <SimpleNotifyModal
        open={forgotPasswdData.isSuccess}
        message={t('users.success.forgot_passwd', {
          userName: forgotPasswdData.name,
          email: forgotPasswdData.email,
        })}
        onOk={() => setForgotPasswdData(forgotPasswdDataInitialValues)}
      />
    </>
  );
});

export const AdminUsersPage = connector(AdminUsersPageComponent);
